import { InfoPopover, Select, Typography } from '@automata/ui';
import { useFlag } from '@automata/utils/src/hooks/featureFlags';
import {
  ParameterDefinition,
  PlannerOptionsInput,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { Stack } from '@mui/system';
import { FC, useState } from 'react';

import { selectShowAdvancedPlannerOptions } from '../../../features/workflow/selectors';
import { useAppSelector } from '../../../hooks';
import { AccordionParameterReference } from '../../ModalAccordion/AccordionParameterReference';
import {
  accordionSx,
  expandIconSx,
} from '../../WorkcellFormModal/styles/workcellFormStyles';

enum Panel {
  NoPanel,
  Settings,
  Parameters,
}

type ParametersData = {
  parameters: ParameterDefinition[];
  parametersWithoutFileType: ParameterDefinition[];
  handleSaveParameter: (
    parameter: ParameterDefinition,
    isValid: boolean
  ) => void;
  handleOptimizationTargetChange: (
    target: PlannerOptionsInput['optimisation_target']
  ) => void;
  optimizationTarget: PlannerOptionsInput['optimisation_target'];
};

const fields = [
  {
    key: 'minimize_total_plan_duration',
    label: 'Minimise total plan duration',
  },
  {
    key: 'minimize_labware_idle_time',
    label: 'Minimise labware idle time',
  },
  {
    key: 'balance_total_plan_duration_and_labware_idle_time',
    label: 'Balance duration and idle time',
  },
];

const PlanWorkflowAccordionGroup: FC<ParametersData> = (
  parametersAccordionData: ParametersData
) => {
  const [expanded, setExpanded] = useState<Panel>(Panel.NoPanel);
  const {
    parameters,
    parametersWithoutFileType,
    handleSaveParameter,
    handleOptimizationTargetChange,
    optimizationTarget,
  } = parametersAccordionData;

  const isOptimiseConsistencyEnabled = useFlag('fe.optimise_consistency');
  const showAdvancedOptions = useAppSelector(selectShowAdvancedPlannerOptions);

  const handlePanelChange = (selectedPanel: Panel) => {
    setExpanded(selectedPanel === expanded ? Panel.NoPanel : selectedPanel);
  };

  return (
    <>
      {isOptimiseConsistencyEnabled && !showAdvancedOptions && (
        <Accordion
          expanded={expanded === Panel.Settings}
          sx={accordionSx}
          onChange={() => handlePanelChange(Panel.Settings)}
        >
          <AccordionSummary sx={{ padding: 0 }}>
            <Typography variant="h6" sx={{ width: '100%' }}>
              Settings
            </Typography>
            <ExpandMoreIcon sx={expandIconSx(expanded === Panel.Settings)} />
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <Stack spacing={2} paddingY={1}>
              <Select
                value={optimizationTarget}
                onChange={(e) =>
                  handleOptimizationTargetChange(
                    e.target.value as PlannerOptionsInput['optimisation_target']
                  )
                }
                label="Optimization target"
                infoPopover={
                  <InfoPopover
                    title="Optimisation Target"
                    copy={
                      <>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Choose how the solver should optimise your run once a
                          valid plan is found.
                        </Typography>
                        <Typography variant="body2">
                          <b>Minimise total plan duration:</b> Finish the run as
                          quickly as possible.
                        </Typography>
                        <Typography variant="body2">
                          <b>Minimise labware idle time:</b> Reduce how long
                          labware sits idle at locations without an active task.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b>Balance duration and idle time:</b> Balance overall
                          run time with time labware spends idle at locations
                          without an active task.
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Note: Optimisation targets are not applied when
                          Advanced Options are selected.
                        </Typography>
                      </>
                    }
                  />
                }
              >
                {fields.map(({ key, label }) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
      {parametersWithoutFileType?.length > 0 && (
        <Accordion
          expanded={expanded === Panel.Parameters}
          sx={accordionSx}
          onChange={() => handlePanelChange(Panel.Parameters)}
        >
          <AccordionSummary sx={{ padding: 0 }}>
            <Typography variant="h6" sx={{ width: '100%' }}>
              Parameters
            </Typography>
            <ExpandMoreIcon sx={expandIconSx(expanded === Panel.Parameters)} />
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <Stack paddingY={1}>
              {parameters.map((param) => (
                <AccordionParameterReference
                  key={param.id}
                  parameter={param}
                  changeParameter={handleSaveParameter}
                />
              ))}
              {parameters.length > parametersWithoutFileType.length && (
                <Typography paddingTop={1} variant="subtitle2">
                  File type parameters are not displayed for preview/planning.
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

PlanWorkflowAccordionGroup.displayName = 'PlanWorkflowAccordionGroup';

export default PlanWorkflowAccordionGroup;
